/** EMBER & PALM — substantial intent pages that help real diners; never mass-produced suburb doorway copy. */
import { Fragment, type ReactNode } from "react";
import { ArrowRight, CalendarDays, MapPin } from "lucide-react";
import { Link } from "wouter";
import { AmokLegendSection } from "@/components/AmokLegend";
import { PageMeta } from "@/components/PageMeta";
import { SITE, trackEvent } from "@/lib/site";

type LandingProps = {
  title: string;
  metaTitle: string;
  description: string;
  path: string;
  eyebrow: string;
  intro: string;
  image: string;
  imageAlt: string;
  sections: { title: string; text: string }[];
  afterFirstSection?: ReactNode;
  faq?: { question: string; answer: string }[];
};

function IntentLanding({
  title,
  metaTitle,
  description,
  path,
  eyebrow,
  intro,
  image,
  imageAlt,
  sections,
  afterFirstSection,
  faq,
}: LandingProps) {
  const faqSchema = faq
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map(item => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      }
    : null;

  return (
    <>
      <PageMeta
        title={metaTitle}
        description={description}
        path={path}
        image={image}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      <section className="intent-hero">
        <div className="container intent-hero-grid">
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h1>{title}</h1>
            <p>{intro}</p>
            <div className="inline-actions">
              <a
                className="button button-gold"
                href={SITE.booking}
                onClick={() =>
                  trackEvent("book_table_click", { placement: path })
                }
              >
                Book a Table <CalendarDays size={17} />
              </a>
              <Link className="text-link light" href="/menu">
                View menu <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <img src={image} alt={imageAlt} width="1600" height="1100" />
        </div>
      </section>
      <section className="section parchment-section">
        <div className="container intent-content">
          <div className="intent-sections">
            {sections.map((section, index) => (
              <Fragment key={section.title}>
                <article>
                  <span>0{index + 1}</span>
                  <div>
                    <h2>{section.title}</h2>
                    <p>{section.text}</p>
                  </div>
                </article>
                {index === 0 && afterFirstSection}
              </Fragment>
            ))}
          </div>
          <aside>
            <MapPin />
            <strong>{SITE.address}</strong>
            <p>Lunch Tuesday–Friday. Dinner Tuesday–Sunday.</p>
            <a
              href={SITE.directions}
              target="_blank"
              rel="noreferrer"
              onClick={() =>
                trackEvent("directions_click", { placement: path })
              }
            >
              Get directions
            </a>
          </aside>
        </div>
      </section>
      {faq && (
        <section className="section faq-section">
          <div className="container">
            <div className="section-heading split-heading">
              <div>
                <p className="eyebrow dark">Fish Amok questions</p>
                <h2>
                  Before your first <em>taste.</em>
                </h2>
              </div>
              <p>
                Clear answers about ANGKOR’s current dish—not an unreviewed home
                recipe.
              </p>
            </div>
            <div className="faq-grid">
              {faq.map(item => (
                <details key={item.question}>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

export function FishAmokPage() {
  return (
    <IntentLanding
      metaTitle="Fish Amok Melbourne | ANGKOR Cambodian Flavours"
      description="Try traditional Fish Amok in Melbourne: fragrant steamed coconut curry with kroeung and mackerel at ANGKOR Cambodian Flavours in West Melbourne."
      path="/fish-amok-melbourne"
      eyebrow="Cambodia’s signature dish"
      title="Fish Amok in Melbourne"
      intro="Softly steamed, fragrant with kroeung and coconut, and served with the warmth of the ANGKOR table."
      image={SITE.images.fishAmok}
      imageAlt="Traditional Fish Amok served in banana leaf at ANGKOR Cambodian Flavours"
      sections={[
        {
          title: "What is Fish Amok?",
          text: "At ANGKOR, Fish Amok is a fragrant steamed coconut curry made with aromatic kroeung, mackerel, Chinese broccoli and peanuts. It is rich and comforting without hiding the freshness of its herbs.",
        },
        {
          title: "A welcoming first taste",
          text: "If Cambodian food is new to you, Fish Amok is an approachable place to begin. Pair it with jasmine rice, a bright salad and a few starters for the table.",
        },
        {
          title: "Made for the table",
          text: "Fish Amok is listed on ANGKOR’s dinner menu and as a weekday lunch rice bowl. Menu details and prices can change, so please check the current menu when planning your visit.",
        },
      ]}
      afterFirstSection={<AmokLegendSection />}
      faq={[
        {
          question: "What is Fish Amok at ANGKOR?",
          answer:
            "ANGKOR’s Fish Amok is a fragrant steamed coconut curry made with aromatic kroeung, mackerel, Chinese broccoli and peanuts.",
        },
        {
          question: "Does ANGKOR’s Fish Amok contain fish or peanuts?",
          answer:
            "Yes. The current ANGKOR dish is made with mackerel and peanuts. Please tell the team about every allergy or dietary requirement before ordering.",
        },
        {
          question: "Can I try Fish Amok at lunch and dinner?",
          answer:
            "Fish Amok is listed on ANGKOR’s dinner menu and as a weekday lunch rice bowl. Menu details and prices can change, so check the current menu before visiting.",
        },
        {
          question: "Does ANGKOR publish a Fish Amok recipe?",
          answer:
            "Not yet. ANGKOR is preparing culturally careful food guides, but no detailed home recipe is published until Chef Neri has reviewed it.",
        },
      ]}
    />
  );
}

export function NearMarvelPage() {
  return (
    <IntentLanding
      metaTitle="Restaurant Near Marvel Stadium & Southern Cross | ANGKOR"
      description="Pre-game dinner or post-event drinks near Marvel Stadium and Southern Cross Station at ANGKOR Cambodian Flavours, 389 Spencer Street, West Melbourne."
      path="/restaurant-near-marvel-stadium-southern-cross"
      eyebrow="Pre & post-event dining"
      title="A Cambodian Restaurant Near Marvel Stadium & Southern Cross"
      intro="Share Fish Amok, Khmer grills and Cambodian-inspired drinks before the game, after the event or on your way through the city."
      image={SITE.images.atmosphere}
      imageAlt="Evening restaurant atmosphere near Marvel Stadium at ANGKOR Cambodian Flavours"
      sections={[
        {
          title: "Book before event nights",
          text: "Major events can make the precinct busy. Reserving ahead gives your group a clear table time before you continue to Marvel Stadium or Southern Cross Station.",
        },
        {
          title: "Dinner made to share",
          text: "Banquets start from $79 per person, while the full menu includes starters, salads, signature mains and desserts for a relaxed pre- or post-event meal.",
        },
        {
          title: "At 389 Spencer Street",
          text: "ANGKOR is in West Melbourne, close to the northern side of the CBD. Use the directions link for a current route from your station exit or venue gate.",
        },
      ]}
    />
  );
}

export function MelbourneRestaurantPage() {
  return (
    <IntentLanding
      metaTitle="Cambodian Restaurant Melbourne CBD | ANGKOR Flavours"
      description="Looking for authentic Cambodian food near Melbourne CBD? Visit ANGKOR at 389 Spencer Street for Fish Amok, Khmer curries, grills and family recipes."
      path="/cambodian-restaurant-melbourne-cbd"
      eyebrow="Genuine Khmer cuisine"
      title="An Authentic Cambodian Restaurant Near Melbourne CBD"
      intro="Not a generic pan-Asian menu: ANGKOR is a proud introduction to Khmer flavours, family recipes and warm Cambodian hospitality."
      image={SITE.images.khmerGrill}
      imageAlt="Shared Khmer grill and noodles at ANGKOR Cambodian Flavours near Melbourne CBD"
      sections={[
        {
          title: "Cambodian, clearly explained",
          text: "Kroeung is a fresh aromatic paste used across Cambodian cooking. Fish Amok is a softly steamed coconut curry. Our team is ready to explain the menu and guide first-time guests.",
        },
        {
          title: "Family recipes and fresh aromatics",
          text: "The menu centres on fragrant herbs, grilled meats, bright salads, slow-cooked dishes and recipes shaped by Chef Neri’s Cambodian heritage.",
        },
        {
          title: "West Melbourne, close to the city",
          text: "Find ANGKOR at 389 Spencer Street, near Southern Cross Station, Docklands and Melbourne CBD. Lunch runs Tuesday–Friday; dinner Tuesday–Sunday.",
        },
      ]}
    />
  );
}
